import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, FileText, Plus, Search, X } from "lucide-react";
import { useSpace, useSpaceNotes, useSpaceTree } from "@/api/hooks";
import type { NoteTreeNode } from "@/api/types";
import { cn, timeAgo } from "@/lib/utils";
import { PageHeader, EmptyState, ErrorState } from "@/components/States";
import { NoteRow } from "@/components/NoteRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageSkeleton } from "@/components/ui/skeleton";

export default function SpacePage() {
  const { id } = useParams<{ id: string }>();
  const space = useSpace(id);
  const [filter, setFilter] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(filter.trim()), 200);
    return () => clearTimeout(t);
  }, [filter]);

  const filtering = debounced.length > 0;
  const tree = useSpaceTree(id);
  const notes = useSpaceNotes(id, debounced);

  if (space.isLoading) return <PageSkeleton />;
  if (space.isError) return <ErrorState error={space.error} retry={() => space.refetch()} />;
  const s = space.data!;
  const noteCount = s.noteCount ?? s.counts?.notes;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="Space"
        title={s.name}
        description={s.description || (noteCount !== undefined ? `${noteCount} ${noteCount === 1 ? "note" : "notes"}` : undefined)}
        actions={
          <Button size="sm" asChild>
            <Link to={`/spaces/${id}/new`}>
              <Plus /> New note
            </Link>
          </Button>
        }
      />

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter notes in this space…" className="pl-9 pr-9" aria-label="Filter notes" />
        {filter && (
          <button type="button" aria-label="Clear filter" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" onClick={() => setFilter("")}>
            <X className="size-4" />
          </button>
        )}
      </div>

      {filtering ? (
        <>
          {notes.isLoading && <PageSkeleton />}
          {notes.isError && <ErrorState error={notes.error} retry={() => notes.refetch()} />}
          {notes.data && notes.data.length === 0 && <EmptyState icon={Search} title={`Nothing matches “${debounced}”`} />}
          {notes.data && notes.data.length > 0 && (
            <div className={cn("flex flex-col", notes.isFetching && "opacity-70")}>
              {[...notes.data].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "")).map((n) => <NoteRow key={n.id} note={n} />)}
            </div>
          )}
        </>
      ) : (
        <>
          {tree.isLoading && <PageSkeleton />}
          {tree.isError && <ErrorState error={tree.error} retry={() => tree.refetch()} />}
          {tree.data && tree.data.length === 0 && (
            <EmptyState
              icon={FileText}
              title="No notes in this space"
              description="Write one here, or ask a model to create one over MCP."
              action={
                <Button size="sm" asChild>
                  <Link to={`/spaces/${id}/new`}>
                    <Plus /> New note
                  </Link>
                </Button>
              }
            />
          )}
          {tree.data && tree.data.length > 0 && (
            <ul className="rounded-xl border bg-card p-1.5">
              {tree.data.map((n) => (
                <TreeRow key={n.id} node={n} depth={0} />
              ))}
            </ul>
          )}
        </>
      )}

    </div>
  );
}

function TreeRow({ node, depth }: { node: NoteTreeNode; depth: number }) {
  const kids = node.children ?? [];
  const [open, setOpen] = useState(depth === 0);
  return (
    <li>
      <div className="group flex items-center gap-1 rounded-lg hover:bg-muted/60" style={{ paddingLeft: depth * 18 }}>
        {kids.length > 0 ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? "Collapse" : "Expand"}
            className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className={cn("size-4 transition-transform", open && "rotate-90")} />
          </button>
        ) : (
          <span className="size-7 shrink-0" />
        )}
        <Link to={`/notes/${node.id}`} className="flex min-w-0 flex-1 items-center gap-2.5 py-2 pr-3 focus-visible:outline-none">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-[0.95rem] font-medium">{node.title || "Untitled"}</span>
          {kids.length > 0 && <span className="shrink-0 text-xs text-muted-foreground">{kids.length}</span>}
          <span className="ml-auto shrink-0 text-xs text-muted-foreground" title={node.updatedAt}>
            {timeAgo(node.updatedAt)}
          </span>
        </Link>
      </div>
      {open && kids.length > 0 && (
        <ul>
          {kids.map((c) => (
            <TreeRow key={c.id} node={c} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}
